import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Candy, CupSoda, Wine, Coffee, Popcorn, SprayCan, Lightbulb, Flame, Sparkles,
  Search, ArrowRight, FileDown, Package,
} from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { CATALOG } from "@/data/catalog";
import { COMPANY } from "@/i18n";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/site/Reveal";

const ICONS = { Candy, CupSoda, Wine, Coffee, Popcorn, SprayCan, Lightbulb, Flame, Sparkles };

export default function CatalogPage() {
  const { t, lang } = useLang();
  const [query, setQuery] = useState("");
  const [active, setActive] = useState("all");
  const location = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
    const hash = location.hash.replace("#", "");
    if (hash && CATALOG.some((c) => c.id === hash)) {
      setActive(hash);
      setTimeout(() => {
        document.getElementById(`cat-${hash}`)?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    }
  }, [location.hash]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return CATALOG.filter((c) => active === "all" || c.id === active).map((c) => ({
      ...c,
      products: q
        ? c.products.filter((p) => p.name.toLowerCase().includes(q) || (p.pack || "").toLowerCase().includes(q))
        : c.products,
    })).filter((c) => c.products.length > 0 || !q);
  }, [query, active]);

  return (
    <main data-testid="catalog-page" className="pt-28 sm:pt-36 pb-24">
      <SEO title={t.seo.catalog.title} description={t.seo.catalog.description} lang={lang} />
      <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
        {/* Header */}
        <Reveal>
          <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.25em] text-[#D4AF37] mb-5">
            {t.nav.catalog} · 2026
          </p>
          <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8">
            <h1 className="font-display font-medium tracking-tighter text-4xl sm:text-6xl lg:text-7xl leading-[0.95] text-white max-w-3xl">
              {t.categories.title}
            </h1>
            <a
              href={COMPANY.catalogPdf}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="catalog-pdf-download"
              className="shrink-0 inline-flex items-center gap-2 px-6 py-3.5 rounded-full border border-white/20 text-white font-semibold hover:border-[#D4AF37]/60 hover:bg-white/5 transition-all"
            >
              <FileDown size={17} /> PDF Katalog
            </a>
          </div>
        </Reveal>

        {/* Search + filters */}
        <div className="mt-12 flex flex-col gap-5 sticky top-16 sm:top-20 z-30 py-4 bg-[#050505]/90 backdrop-blur-md">
          <div className="relative max-w-md">
            <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500" />
            <input
              data-testid="catalog-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={lang === "sr" ? "Pretraži proizvode..." : "Search products..."}
              className="w-full bg-[#0F1012] border border-white/10 rounded-full pl-11 pr-4 py-3 text-white placeholder:text-zinc-600 focus:border-[#D4AF37]/60 focus:outline-none transition-colors"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setActive("all")}
              data-testid="catalog-filter-all"
              className={`px-4 py-2 rounded-full text-sm border transition-all active:scale-95 ${
                active === "all" ? "bg-[#D4AF37] text-black border-[#D4AF37] font-semibold" : "border-white/15 text-zinc-300 hover:border-white/40"
              }`}
            >
              {lang === "sr" ? "Sve" : "All"}
            </button>
            {CATALOG.map((c) => (
              <button
                key={c.id}
                onClick={() => setActive(c.id)}
                data-testid={`catalog-filter-${c.id}`}
                className={`px-4 py-2 rounded-full text-sm border transition-all active:scale-95 ${
                  active === c.id ? "bg-[#D4AF37] text-black border-[#D4AF37] font-semibold" : "border-white/15 text-zinc-300 hover:border-white/40"
                }`}
              >
                {c.name[lang]}
              </button>
            ))}
          </div>
        </div>

        {/* Category blocks */}
        <div className="mt-10 space-y-16">
          {filtered.length === 0 && (
            <div data-testid="catalog-empty" className="py-24 text-center">
              <Package size={40} className="mx-auto text-zinc-700 mb-5" />
              <p className="font-display text-2xl text-white">
                {lang === "sr" ? "Nema rezultata" : "No results"}
              </p>
              <p className="mt-2 text-zinc-500">
                {lang === "sr"
                  ? "Pokušajte sa drugim pojmom ili resetujte filter."
                  : "Try a different term or reset the filter."}
              </p>
              <button
                onClick={() => { setQuery(""); setActive("all"); }}
                className="mt-6 inline-flex items-center px-6 py-3 rounded-full border border-white/20 text-white font-semibold hover:border-[#D4AF37]/60 transition-colors"
              >
                {lang === "sr" ? "Resetuj" : "Reset"}
              </button>
            </div>
          )}
          {filtered.map((cat) => {
            const Icon = ICONS[cat.icon] || Sparkles;
            return (
              <section key={cat.id} id={`cat-${cat.id}`} data-testid={`catalog-section-${cat.id}`} className="scroll-mt-52">
                <div className="flex items-center gap-4 mb-6">
                  <span className="inline-flex items-center justify-center w-12 h-12 rounded-xl border border-white/10 bg-[#0F1012]" style={{ color: cat.accent }}>
                    <Icon size={22} />
                  </span>
                  <div>
                    <h2 className="font-display text-2xl sm:text-3xl font-semibold text-white">{cat.name[lang]}</h2>
                    <p className="text-sm text-zinc-500">{cat.tagline[lang]}</p>
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {cat.products.map((p, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, y: 16 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.4, delay: (i % 6) * 0.03 }}
                      data-testid={`product-${cat.id}-${i}`}
                      className="group flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-[#0F1012] px-5 py-4 hover:border-[#D4AF37]/40 transition-colors"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <Package size={16} className="text-zinc-600 group-hover:text-[#D4AF37] transition-colors shrink-0" />
                        <span className="text-white font-medium truncate">{p.name}</span>
                      </div>
                      {p.pack && <span className="text-xs font-mono text-zinc-500 shrink-0">{p.pack}</span>}
                    </motion.div>
                  ))}
                </div>
              </section>
            );
          })}
        </div>

        {/* CTA */}
        <Reveal>
          <div className="mt-20 rounded-3xl border border-white/10 bg-gradient-to-br from-[#0F1012] to-black p-10 sm:p-14 text-center">
            <h3 className="font-display text-3xl sm:text-4xl font-medium tracking-tight text-white">
              {lang === "sr" ? "Zainteresovani za ponudu?" : "Interested in a quote?"}
            </h3>
            <p className="mt-4 text-zinc-400 max-w-xl mx-auto">
              {lang === "sr"
                ? "Pošaljite upit sa kategorijama koje vas zanimaju i javljamo vam se sa veleprodajnim cenama."
                : "Send an inquiry with the categories you need and we'll reply with wholesale pricing."}
            </p>
            <Link
              to="/#inquiry"
              data-testid="catalog-cta-inquiry"
              className="mt-8 group inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#D4AF37] text-black font-bold hover:bg-[#F3CE5A] active:scale-95 transition-all"
            >
              {t.hero.ctaPrimary}
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
